import { useEffect, useId, useLayoutEffect, useRef, useState, type JSX } from 'react';

import { cn } from '../lib/cn.js';
import { IconReplyThread } from '../icons/custom/icon-reply-thread.js';
import { intersection, placementDuChoixDeReaction, type RectMesure } from './placementDuChoixDeReaction.js';
import { AUTRES_REACTIONS, LIBELLES_DE_REACTION, REACTIONS_DE_LA_PILULE, reactionsComptees, type CleDeReaction, type ReactionsDuMessage } from './reactions.js';

export interface BarreDActionsDuMessageProps {
  /** Qui a reagi, par cle (`message.reactions`). */
  reactions: ReactionsDuMessage | undefined;
  /** Le personId de la personne connectee : ses reactions sont « pressees ». */
  moi: string | null;
  /** Absent : pas de reactions (lecture seule, compte sans fiche). */
  onReagir?: ((cle: CleDeReaction, dejaPosee: boolean) => void) | undefined;
  /** Absent : pas de bouton de fil (une reponse, ou une vue sans fil). */
  onRepondre?: (() => void) | undefined;
  /** « Repondre dans le fil » ou, en lecture seule, « Voir le fil ». */
  libelleRepondre?: string | undefined;
  /** La variante compacte du centre. */
  compacte?: boolean | undefined;
}

/** Ce que les parents laissent voir de `depart` : l'intersection des ancetres qui rognent (overflow non visible) et de la fenetre. */
function zoneVisible(depart: HTMLElement): RectMesure {
  const fenetre = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
  const rects: RectMesure[] = [fenetre];
  for (let parent = depart.parentElement; parent !== null; parent = parent.parentElement) {
    const style = window.getComputedStyle(parent);
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') rects.push(parent.getBoundingClientRect());
  }
  return intersection(rects) ?? fenetre;
}

/**
 * Deux petits ronds au bord de la bulle, au survol : un smiley qui ouvre les
 * reactions, et « Repondre dans le fil » (seulement quand le message a un fil
 * possible). Ils ne se montrent qu'a la demande, sans deplacer les messages.
 * Le smiley ouvre une PILULE blanche posee AU-DESSUS de la bulle (en dessous
 * s'il n'y a pas la place), comme sur WhatsApp : quatre emojis a l'echelle du texte et un
 * « + » rond gris. Le « + » REMPLACE les quatre par les autres reactions (`AUTRES_REACTIONS`),
 * et un rond de retour les rend. La pilule se POSE EN JS, sur la bulle de son
 * message (`placementDuChoixDeReaction`) : alignee sur son bord (gauche si recu,
 * droite si envoye) et bornee dans la zone que les parents laissent voir, donc
 * jamais rognee. Elle est recalculee au defilement et au redimensionnement tant
 * qu'elle est ouverte, et a chaque changement de rangee.
 */
export function BarreDActionsDuMessage({ reactions, moi, onReagir, onRepondre, libelleRepondre = 'Répondre dans le fil', compacte }: BarreDActionsDuMessageProps): JSX.Element | null {
  const [ouvert, setOuvert] = useState(false);
  const [autres, setAutres] = useState(false);
  const barre = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const choix = useRef<HTMLDivElement>(null);
  const choixId = useId();
  useLayoutEffect(() => {
    if (!ouvert) return;
    const placer = () => {
      const boite = choix.current;
      const ancre = barre.current;
      if (boite === null || ancre === null) return;
      const hote = ancre.closest<HTMLElement>('[data-barre-message-hote]');
      const bulle = (hote?.querySelector<HTMLElement>('[data-bulle]') ?? hote ?? ancre).getBoundingClientRect();
      const origine = ancre.getBoundingClientRect();
      const { left, top } = placementDuChoixDeReaction({
        bulle, conteneur: zoneVisible(ancre), choix: { largeur: boite.offsetWidth, hauteur: boite.offsetHeight },
        cote: ancre.closest('[data-message-moi]') !== null ? 'droite' : 'gauche', origine,
      });
      boite.style.left = `${left}px`;
      boite.style.top = `${top}px`;
    };
    placer();
    window.addEventListener('resize', placer);
    document.addEventListener('scroll', placer, { capture: true, passive: true });
    return () => { window.removeEventListener('resize', placer); document.removeEventListener('scroll', placer, { capture: true }); };
  }, [ouvert, autres]);
  useEffect(() => {
    if (!ouvert) return;
    choix.current?.querySelector('button')?.focus({ preventScroll: true });
  }, [ouvert, autres]);
  useEffect(() => {
    if (!ouvert) return;
    const dehors = (event: PointerEvent) => { if (!barre.current?.contains(event.target as Node)) setOuvert(false); };
    document.addEventListener('pointerdown', dehors);
    return () => document.removeEventListener('pointerdown', dehors);
  }, [ouvert]);
  const fermer = (auClavier = true) => {
    setOuvert(false);
    if (auClavier) bouton.current?.focus({ preventScroll: true });
  };
  if (onReagir === undefined && onRepondre === undefined) return null;
  const proposees = autres ? AUTRES_REACTIONS : REACTIONS_DE_LA_PILULE;
  return <div ref={barre} className={cn('taki-barre-message', compacte && 'taki-barre-message-compacte')} role="group" aria-label="Actions du message" data-barre-message data-ouvert={ouvert || undefined}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOuvert(false); }}
    onKeyDown={event => { if (event.key === 'Escape' && ouvert) { event.preventDefault(); event.stopPropagation(); fermer(); } }}>
    {onReagir !== undefined && <button ref={bouton} type="button" aria-label={LIBELLE_REAGIR} title={LIBELLE_REAGIR}
      aria-expanded={ouvert} aria-controls={ouvert ? choixId : undefined} aria-haspopup="dialog"
      onClick={() => { setAutres(false); setOuvert(value => !value); }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" /><path d="M8.5 14.5c1.5 2 5.5 2 7 0" /><path d="M8.5 9h.01M15.5 9h.01" strokeWidth="2.8" />
      </svg>
    </button>}
    {onRepondre !== undefined && <button type="button" onClick={() => { setOuvert(false); onRepondre(); }} aria-label={libelleRepondre} title={libelleRepondre}><IconReplyThread size={18} /></button>}
    {ouvert && onReagir !== undefined && <div ref={choix} id={choixId} role="dialog" aria-label="Réactions" className="taki-choix-reaction" data-rangee={autres ? 'autres' : 'pilule'}>
      {proposees.map(({ cle, emoji }) => {
        const posee = moi !== null && (reactions?.[cle] ?? []).includes(moi);
        return <button key={cle} type="button" onClick={event => { onReagir(cle, posee); fermer(event.detail === 0); }} aria-pressed={posee}
          aria-label={libelleDuGeste(cle, posee)} title={libelleDuGeste(cle, posee)}><span aria-hidden="true">{emoji}</span></button>;
      })}
      <button type="button" className="taki-choix-reaction-plus" onClick={() => setAutres(value => !value)}
        aria-label={autres ? 'Revenir aux réactions principales' : 'Plus de réactions'} title={autres ? 'Revenir aux réactions principales' : 'Plus de réactions'}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {autres ? <path d="M14.5 6.5 9 12l5.5 5.5" /> : <path d="M12 5.5v13M5.5 12h13" />}
        </svg>
      </button>
    </div>}
  </div>;
}

/** Le smiley qui ouvre la pilule : exporte pour le test et pour les captures. */
export const LIBELLE_REAGIR = 'Ajouter une réaction';

/** Le libelle du geste sur une reaction : « J’approuve », ou « Retirer : J’approuve » si elle est deja posee. Exporte pour le test. */
export function libelleDuGeste(cle: CleDeReaction, posee: boolean): string {
  return posee ? `Retirer : ${LIBELLES_DE_REACTION[cle]}` : LIBELLES_DE_REACTION[cle];
}

export interface PastillesDeReactionsProps {
  reactions: ReactionsDuMessage | undefined;
  moi: string | null;
  /** Absent : les pastilles se lisent, ne se cliquent pas. */
  onReagir?: ((cle: CleDeReaction, dejaPosee: boolean) => void) | undefined;
  className?: string | undefined;
}

/**
 * LES COMPTEURS DE REACTIONS sous le message : un emoji et son nombre, presse
 * quand la personne connectee en fait partie ; un clic pose ou retire la
 * sienne. Rien quand personne n'a reagi (aucun espace vide).
 */
export function PastillesDeReactions({ reactions, moi, onReagir, className }: PastillesDeReactionsProps): JSX.Element | null {
  const posees = reactionsComptees(reactions, moi);
  if (posees.length === 0) return null;
  return <div className={cn('mt-2 flex flex-wrap gap-1', className)} data-reactions>
    {posees.map(({ cle, emoji, personnes, deMoi }) => <button key={cle} type="button" disabled={onReagir === undefined}
      onClick={() => onReagir?.(cle, deMoi)} aria-pressed={deMoi}
      aria-label={`${LIBELLES_DE_REACTION[cle]}, ${personnes.length} réaction${personnes.length > 1 ? 's' : ''}`} title={LIBELLES_DE_REACTION[cle]}
      className={cn('flex h-6 items-center gap-1.5 rounded-pill px-2 text-xs font-medium tabular-nums outline-none focus-visible:shadow-ring disabled:cursor-default',
        deMoi ? 'bg-accent-tint-border text-ink-900 enabled:hover:bg-accent-tint' : 'bg-surface-3 text-ink-600 enabled:hover:bg-accent-tint')}>
      <span aria-hidden="true">{emoji}</span><span>{personnes.length}</span>
    </button>)}
  </div>;
}
